import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
} from "chart.js";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";
type Map = mapboxgl.Map;
type MarkerType = mapboxgl.Marker;
import { colors } from "@/styles/colors";
import { metersToFeet, metersToMiles } from "@/utils/displayFormatUtils";
import { z } from "zod";
import { TrackPoint } from "@/apiTypes/event";

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
);

type TrackPointType = z.infer<typeof TrackPoint>;

interface ElevationChartProps {
  trackPoints: TrackPointType[];
  map: Map;
}

/**
 * Downsamples an array of track points to a target number of points
 * using uniform sampling.
 */
function downsample(
  points: TrackPointType[],
  targetCount: number,
): TrackPointType[] {
  if (points.length <= targetCount) return points;
  const step = (points.length - 1) / (targetCount - 1);
  const result: TrackPointType[] = [];
  for (let i = 0; i < targetCount; i++) {
    result.push(points[Math.round(i * step)]);
  }
  return result;
}

/**
 * RWGPS-style elevation profile chart that overlays the bottom of the map.
 * Shows elevation vs distance with a filled area chart and hover interaction
 * that places a marker on the map at the corresponding position.
 */
export const ElevationChart: FC<ElevationChartProps> = ({
  trackPoints,
  map,
}) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart<"line", number[], string> | null>(null);
  const markerRef = useRef<MarkerType | null>(null);
  const sampledRef = useRef<TrackPointType[]>([]);

  const sampled = useMemo(() => {
    const valid = trackPoints.filter(
      (p) =>
        p.ele !== undefined &&
        p.d !== undefined &&
        p.lat !== undefined &&
        p.lng !== undefined,
    );
    return downsample(valid, 500);
  }, [trackPoints]);

  // Keep ref in sync for use in Chart.js callbacks
  sampledRef.current = sampled;

  const lastHoverIdx = useRef<number | null>(null);

  /** Removes the hover marker from the map. */
  const removeMarker = useCallback(() => {
    lastHoverIdx.current = null;
    markerRef.current?.remove();
    markerRef.current = null;
  }, []);

  const handleHover = useCallback(
    (_event: unknown, elements: { index: number }[]) => {
      if (elements.length > 0) {
        const idx = elements[0].index;
        // Skip if index hasn't changed since last hover
        if (idx === lastHoverIdx.current) return;
        lastHoverIdx.current = idx;

        const pt = sampledRef.current[idx];
        if (pt?.lng !== undefined && pt?.lat !== undefined) {
          if (!markerRef.current) {
            const el = document.createElement("div");
            el.style.width = "16px";
            el.style.height = "16px";
            el.style.borderRadius = "50%";
            el.style.backgroundColor = colors.primary;
            el.style.border = "2.5px solid white";
            el.style.boxShadow = "0 1px 4px rgba(0,0,0,0.35)";
            markerRef.current = new mapboxgl.Marker({ element: el })
              .setLngLat([pt.lng, pt.lat])
              .addTo(map);
          } else {
            markerRef.current.setLngLat([pt.lng, pt.lat]);
          }
        }
      } else {
        removeMarker();
      }
    },
    [map],
  );

  useEffect(() => {
    if (!chartRef.current || sampled.length < 2) return;

    // Already initialized — skip rebuild
    if (chartInstanceRef.current) return;

    const elevations = sampled.map((p) => Math.round(metersToFeet(p.ele!)));
    const rawMin = Math.min(...elevations);
    const rawMax = Math.max(...elevations);
    const range = rawMax - rawMin;
    const step = range > 2000 ? 500 : range > 500 ? 100 : range > 100 ? 50 : 25;
    const minEle = Math.max(
      0,
      Math.floor((rawMin - range * 0.1) / step) * step,
    );
    const maxEle = Math.ceil((rawMax + range * 0.1) / step) * step;

    const totalDistMi = metersToMiles(sampled[sampled.length - 1].d!);
    const labels = sampled.map((p) => `${Math.round(metersToMiles(p.d!))}`);

    chartInstanceRef.current = new Chart<"line", number[], string>(
      chartRef.current,
      {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              data: elevations,
              fill: true,
              backgroundColor: `${colors.primary}40`,
              borderColor: colors.primary,
              borderWidth: 1.5,
              tension: 0.2,
              pointRadius: 0,
              pointHoverRadius: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              displayColors: false,
              backgroundColor: "rgba(0,0,0,0.75)",
              titleFont: { size: 11 },
              bodyFont: { size: 11 },
              padding: 6,
              callbacks: {
                title: (items) => {
                  const idx = items[0]?.dataIndex;
                  if (idx === undefined) return "";
                  const dist = sampledRef.current[idx]?.d;
                  if (dist === undefined) return "";
                  return `${metersToMiles(dist).toFixed(1)} mi`;
                },
                label: (item) => `${Math.round(item.raw as number)} ft`,
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              border: { display: false },
              ticks: {
                color: "#666",
                font: { size: 9 },
                maxRotation: 0,
                autoSkip: true,
                maxTicksLimit: 8,
                padding: 2,
                callback: function (_value, index) {
                  const points = sampledRef.current;
                  const dist = points[index]?.d;
                  if (dist === undefined) return "";
                  const mi = metersToMiles(dist);
                  // Always show last tick
                  if (index === points.length - 1) {
                    return `${Math.round(mi)}`;
                  }
                  const interval =
                    totalDistMi > 100
                      ? 25
                      : totalDistMi > 50
                        ? 10
                        : totalDistMi > 20
                          ? 5
                          : 2;
                  if (Math.abs(mi - Math.round(mi / interval) * interval) < 1) {
                    return `${Math.round(mi)}`;
                  }
                  return "";
                },
              },
            },
            y: {
              min: minEle,
              max: maxEle,
              grid: { color: "rgba(0,0,0,0.06)" },
              border: { display: false },
              afterBuildTicks: (axis) => {
                const ticks = axis.ticks;
                if (ticks.length === 0) return;
                // Ensure min and max ticks are present
                if (ticks[0].value !== minEle) {
                  ticks.unshift({ value: minEle });
                }
                if (ticks[ticks.length - 1].value !== maxEle) {
                  ticks.push({ value: maxEle });
                }
              },
              ticks: {
                color: "#666",
                font: { size: 9 },
                maxTicksLimit: 5,
                padding: 2,
                callback: (value) => `${value}`,
              },
            },
          },
          layout: {
            padding: { top: 2, right: 4, bottom: 0, left: 0 },
          },
          onHover: handleHover,
        },
      },
    );

    const canvas = chartRef.current;
    canvas.addEventListener("mouseleave", removeMarker);

    return () => {
      canvas.removeEventListener("mouseleave", removeMarker);
      chartInstanceRef.current?.destroy();
      chartInstanceRef.current = null;
      markerRef.current?.remove();
      markerRef.current = null;
    };
  }, [sampled, map, handleHover, removeMarker]);

  if (sampled.length < 2) return null;

  return (
    <div className="pointer-events-auto flex h-full w-full flex-col px-2 pb-1">
      <div className="relative min-h-0 min-w-0 flex-1">
        <canvas ref={chartRef} className="absolute inset-0" />
      </div>
    </div>
  );
};
